/**
 * reviewDiff - which words a finding actually changes (F-REVIEW).
 *
 * A finding's `original_text` is a whole subsection and its
 * `suggested_redline` a full rewrite of it, so showing one struck through
 * above the other makes the reader find the difference. This works it out:
 * a word-level diff of the two, reduced to the changed HUNKS with a few words
 * of context either side - enough to find each hunk again inside the clause
 * in the document, and to show "30 days → 60 days" instead of two paragraphs.
 *
 * When most of the text changes it is a rewrite, not an edit; callers then
 * fall back to treating the whole clause as the change.
 */

export type DiffSegment = { type: 'equal' | 'removed' | 'added'; text: string };

export interface DiffHunk {
  /** Unchanged words just before the change (for finding it, and for display). */
  before: string;
  deleted: string;
  inserted: string;
  after: string;
}

export interface ReviewChange {
  segments: DiffSegment[];
  hunks: DiffHunk[];
  /** Share of the original's words that were removed or replaced (0-1). */
  changeRatio: number;
  /** Most of the clause changed - highlight the clause, not words. */
  isRewrite: boolean;
}

/** Words and the whitespace between them, so the diff re-joins exactly. */
export const tokenize = (text: string): string[] => text.match(/\S+|\s+/g) || [];

const isSpace = (s: string) => /^\s+$/.test(s);

/**
 * "5.4 The Supplier shall…" vs "The Supplier shall…": a section number on
 * one side only is not a change anyone made.
 */
export const stripLeadingNumbering = (text: string): string =>
  text.replace(/^\s*(?:\(?[0-9]+(?:\.[0-9]+)*\)?[.)]?|\(?[a-z]\)|[ivx]+\))\s+/i, '');

/** Merge adjacent segments of the same type. */
const merge = (segs: DiffSegment[]): DiffSegment[] => {
  const out: DiffSegment[] = [];
  for (const s of segs) {
    if (!s.text) continue;
    const last = out[out.length - 1];
    if (last && last.type === s.type) last.text += s.text;
    else out.push({ ...s });
  }
  return out;
};

/**
 * Word-level LCS diff. The common prefix and suffix are cut first, so a
 * 4000-character clause with one changed word costs almost nothing.
 */
export const buildDiff = (original: string, modified: string): DiffSegment[] => {
  const a = tokenize(original);
  const b = tokenize(modified);

  let start = 0;
  while (start < a.length && start < b.length && a[start] === b[start]) start++;
  let endA = a.length;
  let endB = b.length;
  while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) { endA--; endB--; }

  const midA = a.slice(start, endA);
  const midB = b.slice(start, endB);
  const m = midA.length;
  const n = midB.length;
  const dp: Uint32Array[] = Array.from({ length: m + 1 }, () => new Uint32Array(n + 1));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = midA[i - 1] === midB[j - 1] ? dp[i - 1][j - 1] + 1 : Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }
  const mid: DiffSegment[] = [];
  let i = m;
  let j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && midA[i - 1] === midB[j - 1]) { mid.push({ type: 'equal', text: midA[i - 1] }); i--; j--; }
    else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) { mid.push({ type: 'added', text: midB[j - 1] }); j--; }
    else { mid.push({ type: 'removed', text: midA[i - 1] }); i--; }
  }
  mid.reverse();

  // A lone space between two changes reads as part of one change.
  const absorbed = mid.map((s, k) =>
    s.type === 'equal' && isSpace(s.text) && mid[k - 1] && mid[k + 1]
      && mid[k - 1].type !== 'equal' && mid[k + 1].type !== 'equal'
      ? [{ type: 'removed' as const, text: s.text }, { type: 'added' as const, text: s.text }]
      : [s]).flat();

  return merge([
    { type: 'equal', text: a.slice(0, start).join('') },
    ...absorbed,
    { type: 'equal', text: a.slice(endA).join('') },
  ]);
};

const lastWords = (text: string, n: number) => {
  const toks = tokenize(text);
  let words = 0;
  let k = toks.length;
  while (k > 0 && words < n) { k--; if (!isSpace(toks[k])) words++; }
  return toks.slice(k).join('');
};

const firstWords = (text: string, n: number) => {
  const toks = tokenize(text);
  let words = 0;
  let k = 0;
  while (k < toks.length && words < n) { if (!isSpace(toks[k])) words++; k++; }
  return toks.slice(0, k).join('');
};

/**
 * The change a finding makes, as hunks with context.
 *
 * `context` words either side of each hunk; hunks closer than that merge.
 */
export const reviewChange = (
  original: string | null | undefined,
  replacement: string | null | undefined,
  context = 5,
  rewriteThreshold = 0.6,
): ReviewChange | null => {
  const from = stripLeadingNumbering(original || '').trim();
  const to = stripLeadingNumbering(replacement || '').trim();
  if (!from || !to) return null;

  const segments = buildDiff(from, to);
  const hunks: DiffHunk[] = [];
  let removedWords = 0;
  const totalWords = tokenize(from).filter((t) => !isSpace(t)).length || 1;

  let k = 0;
  while (k < segments.length) {
    if (segments[k].type === 'equal') { k++; continue; }
    let deleted = '';
    let inserted = '';
    // Gather the change, swallowing short equal runs (< context words)
    // between changes so near edits make one hunk.
    let j = k;
    while (j < segments.length) {
      const s = segments[j];
      if (s.type === 'removed') { deleted += s.text; removedWords += tokenize(s.text).filter((t) => !isSpace(t)).length; }
      else if (s.type === 'added') inserted += s.text;
      else {
        const words = tokenize(s.text).filter((t) => !isSpace(t)).length;
        const next = segments[j + 1];
        if (words < context && next && next.type !== 'equal') { deleted += s.text; inserted += s.text; }
        else break;
      }
      j++;
    }
    const prev = segments[k - 1];
    const next = segments[j];
    hunks.push({
      before: prev && prev.type === 'equal' ? lastWords(prev.text, context) : '',
      deleted,
      inserted,
      after: next && next.type === 'equal' ? firstWords(next.text, context) : '',
    });
    k = j;
  }

  const changeRatio = Math.min(1, removedWords / totalWords);
  return { segments, hunks, changeRatio, isRewrite: changeRatio > rewriteThreshold };
};
